import { decodePinKey, encodePinKey } from './pinCrypto.helpers';
import { normaliseEmail, PinKeyCacheEntry, readUsersCache, writeUsersCache } from './usersCache.helpers';

export type { PinKeyCacheEntry, PinTokenStore } from './usersCache.helpers';

const ACTIVE_KEY = 'pin_login_active';

const keystoreQueues = new Map<string, Promise<unknown>>();

// Runs keystore writes and deletes of one pin.key in order, so a late upload can't undo a later change.
export const runKeystoreMutation = <T>(pinKeyPath: string, mutation: () => Promise<T>): Promise<T> => {
  const previous = keystoreQueues.get(pinKeyPath) ?? Promise.resolve();
  const run = previous.then(mutation);
  const settled: Promise<unknown> = run
    .catch(() => undefined)
    .finally(() => {
      if (keystoreQueues.get(pinKeyPath) === settled) keystoreQueues.delete(pinKeyPath);
    });
  keystoreQueues.set(pinKeyPath, settled);
  return run;
};

export const getCachedPinKey = (orgId: string, email: string): PinKeyCacheEntry | undefined =>
  readUsersCache()[orgId]?.users[normaliseEmail(email)]?.pin;

export const setCachedPinKey = (orgId: string, email: string, entry: PinKeyCacheEntry): void => {
  const cache = readUsersCache();
  const org = cache[orgId] ?? { name: '', users: {} };
  const key = normaliseEmail(email);
  const user = org.users[key] ?? { userId: entry.userId, email };
  writeUsersCache({ ...cache, [orgId]: { ...org, users: { ...org.users, [key]: { ...user, pin: entry } } } });
};

export const removeCachedPinKey = (orgId: string, email: string): void => {
  const cache = readUsersCache();
  const key = normaliseEmail(email);
  const user = cache[orgId]?.users[key];
  if (!user) return;

  const { pin: _removed, ...rest } = user;
  writeUsersCache({ ...cache, [orgId]: { ...cache[orgId], users: { ...cache[orgId].users, [key]: rest } } });
};

export const clearOrgPinKeys = (orgId: string): void => {
  const cache = readUsersCache();
  const org = cache[orgId];
  if (!org) return;

  const users = Object.fromEntries(Object.entries(org.users).map(([key, { pin: _removed, ...rest }]) => [key, rest]));
  writeUsersCache({ ...cache, [orgId]: { ...org, users } });
};

export const hasCachedPinKey = (orgId: string | null | undefined, email: string): boolean => {
  const entry = orgId ? getCachedPinKey(orgId, email) : undefined;
  return !!entry && !entry.pendingDelete;
};

const DEFAULT_LOCKOUT_SECONDS = 5 * 60;

// Remembers a pepper lockout so this browser can show when PIN entry and setup work again.
export const setPinLockout = (
  orgId: string,
  email: string,
  retryAfterSeconds: number = DEFAULT_LOCKOUT_SECONDS,
  userId?: string,
): void => {
  const cache = readUsersCache();
  const org = cache[orgId] ?? { name: '', users: {} };
  const key = normaliseEmail(email);
  const user = org.users[key] ?? (userId ? { userId, email } : undefined);
  if (!user) return;

  const lockedUntilEpochMs = Date.now() + retryAfterSeconds * 1000;
  writeUsersCache({ ...cache, [orgId]: { ...org, users: { ...org.users, [key]: { ...user, lockedUntilEpochMs } } } });
};

export const getPinLockoutEnd = (
  orgId: string | null | undefined,
  email: string,
  now = Date.now(),
): number | undefined => {
  const until = orgId ? readUsersCache()[orgId]?.users[normaliseEmail(email)]?.lockedUntilEpochMs : undefined;
  return until && until > now ? until : undefined;
};

export const isCachedRtExpired = (orgId: string, email: string, now = Date.now()): boolean => {
  const entry = getCachedPinKey(orgId, email);
  return !!entry && entry.expiryEpochMs <= now;
};

/** pin.key as stored in the keystore bucket. */
export const pinKeyEntryToBlob = ({ tokenStore, expiryEpochMs }: PinKeyCacheEntry): string =>
  encodePinKey({ v: 1, ...tokenStore, rtExpiresAt: expiryEpochMs });

export const pinKeyEntryFromBlob = (
  { userId, pinKeyPath }: Pick<PinKeyCacheEntry, 'userId' | 'pinKeyPath'>,
  blob: string,
): PinKeyCacheEntry => {
  const { salt, iv, ciphertext, rtExpiresAt } = decodePinKey(blob);
  return { userId, pinKeyPath, expiryEpochMs: rtExpiresAt, tokenStore: { salt, iv, ciphertext } };
};

// lets the logged-out page know PIN login is on for this device, when no session can tell it
export const setPinLoginActiveOnDevice = (active: boolean): void => {
  if (active) localStorage.setItem(ACTIVE_KEY, 'true');
  else localStorage.removeItem(ACTIVE_KEY);
};

export const isPinLoginActiveOnDevice = (): boolean => localStorage.getItem(ACTIVE_KEY) === 'true';

// firebase storage and the savi-api passthrough report a missing pin.key differently
export const isPinKeyNotFoundError = (error: unknown): boolean => {
  const { code, response } = (error ?? {}) as { code?: string; response?: { status?: number } };
  return code === 'storage/object-not-found' || response?.status === 404;
};
